import { useState } from 'react';
import { Outlet } from 'react-router-dom';
import { useUIStore } from '../../shared/store/uiStore';
import { useAuthStore } from '../../features/auth/store/authStore';
import { useSidebarVisibility } from '../../shared/hooks/useSidebarVisibility';
import { useMediaQuery, BREAKPOINT_LG } from '../../shared/hooks/useMediaQuery';
import { AppSidebar } from './components/AppSidebar';
import { AppHeader } from './components/AppHeader';
import BulkImportStatusIndicator from '../../features/team/components/BulkImport/BulkImportStatusIndicator';
import { IdleTimeoutDialog } from '../../features/auth/components/IdleTimeoutDialog';

export function AppLayout() {
  // The rail's expanded/collapsed state is the store's `isSidebarOpen`, not local state: it is a
  // deliberate choice the user makes with the toggle, so anything else that needs to know (a page
  // measuring its own width, a future header control) can read the same flag. Starts collapsed.
  const { toggleTheme, isSidebarOpen, toggleSidebar } = useUIStore();
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  // The rail needs room to expand to 270px beside the content, so it only earns
  // its place at lg. Below that — phones and portrait tablets — the hamburger
  // drawer is the better fit. Same breakpoint the listing pages
  // use to swap tables for cards, so the whole app changes shape at once.
  const isDesktop = useMediaQuery(BREAKPOINT_LG);

  // Initialize sidebar visibility hook (fetches from API and populates store). Keyed by the active
  // site so a site switch refetches the new site's visibility instead of serving the previous site's
  // from the 5-minute cache. Read here (app layer) rather than in the shared store, which must not
  // depend on the auth feature store. Null for single-site customers (no site selection).
  const activeSiteId = useAuthStore((s) => s.selectedSite?.haloPsaSiteId ?? null);
  useSidebarVisibility(activeSiteId);

  // Rotating a tablet to landscape crosses into the rail layout; without this the
  // drawer would still be open when it rotates back. Adjusted during render rather
  // than in an effect so there is no throwaway pass with the stale value.
  const [wasDesktop, setWasDesktop] = useState(isDesktop);
  if (wasDesktop !== isDesktop) {
    setWasDesktop(isDesktop);
    if (isDesktop && isDrawerOpen) setIsDrawerOpen(false);
  }

  return (
    // No `dark` class here: the mode lives on <html> so portalled UI (dialogs,
    // dropdowns) inherits the tokens too.
    //
    // `fixed inset-0` rather than `h-screen`: the shell is a frame, not page content.
    // In flow it shared a document with the helper nodes third-party SDKs (Stripe,
    // Keycloak) append to <body>; when one of those lays out in flow the document grows
    // taller than the shell, so scrolling lifts the whole app and exposes a band of body
    // background beneath it. Out of flow the shell always covers the viewport exactly.
    // It also drops the 100vh-vs-URL-bar gap on real mobile browsers that h-screen has.
    <div className="fixed inset-0 flex flex-col overflow-hidden" data-testid="app-layout-container">
      {/* Header spans full width on top */}
      <AppHeader
        showMenuButton={!isDesktop}
        onThemeToggle={toggleTheme}
        onMobileMenuToggle={() => setIsDrawerOpen(true)}
      />

      {/* Sidebar + Main content below header */}
      <div className="flex flex-1 min-h-0 overflow-hidden">
        {/* One shape at a time, chosen in JS rather than hidden with CSS — see useMediaQuery.
            Rendering both would mount two sidebars, and so two firm selectors with their own
            dropdown state and duplicate test ids. */}
        {isDesktop ? (
          <AppSidebar isCollapsed={!isSidebarOpen} onToggleCollapse={toggleSidebar} />
        ) : (
          <>
            {isDrawerOpen && (
              <button
                type="button"
                className="fixed inset-0 bg-black/50 z-40 appearance-none border-0 p-0 m-0"
                onClick={() => setIsDrawerOpen(false)}
                aria-label="Close menu"
                data-testid="mobile-sidebar-overlay"
              />
            )}

            <div
              className={`fixed top-0 left-0 h-full z-50 transform transition-transform duration-300 ease-in-out ${
                isDrawerOpen ? 'translate-x-0' : '-translate-x-full'
              }`}
            >
              <AppSidebar
                isCollapsed={false}
                isMobile={true}
                onClose={() => setIsDrawerOpen(false)}
              />
            </div>
          </>
        )}

        <main className="flex-1 overflow-y-auto bg-surface-page" data-testid="app-main-content">
          <Outlet />
        </main>
      </div>

      {/* Mounted at the shell so a bulk import keeps being polled and reported across every route,
          not only while the Team page's drawer happens to be open. Renders nothing when idle. */}
      <BulkImportStatusIndicator />

      {/* Idle auto-logout: here, inside ProtectedRoute, so it only ever runs for a signed-in user. */}
      <IdleTimeoutDialog />
    </div>
  );
}
